import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import type { AUTDevice } from "@/api/queries/account";
import { ControllerConfigurationPanel, type ControllerConfigValue } from "@/components/ControllerConfigurationPanel";
import { DeviceActivationPanel } from "@/components/DeviceActivationPanel";
import Button from "@/components/ui/Button";
import { propertiesAtom } from "@/store";
import { cropTypesAtom, deviceToLicMapAtom, propertyDevicesAtom } from "@/store/data";
import type { VirtualDeviceConfigurationWithModel } from "@/store/data/property-domain";
import { isMeshDevice } from "@/utils/mesh-device-utils";
import ConfirmModal from "../../../components/ConfirmModal";

interface SectorDetailPanelProps {
  sector?: {
    id: string;
    name: string;
    description: string;
    area: number;
    power: number;
    latitude: number | null;
    longitude: number | null;
    altitude: number | null;
    currentCropTypeId: string | null;
    currentCropStartDate: string | null;
    currentCropPlantPopulation: number | null;
    currentCropPlantingDate: string | null;
  };
  projectId: string;
  devices: AUTDevice[];
  currentVirtualDeviceConfig?: VirtualDeviceConfigurationWithModel | null;
  hasAnalogPumpMode: boolean;
  onSave: (
    sectorData: {
      name: string;
      description: string;
      area: number;
      power: number;
      coordinates: { type: "Point"; coordinates: [number, number] } | null;
      altitude: number | null;
      cropTypeId: string | null;
      cropStartDate: string | null;
      cropPlantPopulation: number | null;
      cropPlantingDate: string | null;
    },
    controllerConfig: ControllerConfigValue | null
  ) => void;
  onDelete?: (sectorId: string) => void;
  onCancel: () => void;
}

/**
 * SectorDetailPanel component for creating and editing sectors.
 *
 * This component uses ControllerConfigurationPanel for valve controller configuration
 * and handles sector-specific fields like name, description, area, and power.
 */
function SectorDetailPanel({
  sector,
  projectId,
  devices,
  currentVirtualDeviceConfig,
  hasAnalogPumpMode,
  onSave,
  onDelete,
  onCancel,
}: SectorDetailPanelProps) {
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    name: sector?.name || "",
    description: sector?.description || "",
    area: sector?.area?.toString() || "",
    power: sector?.power?.toString() || "0",
    latitude: sector?.latitude?.toString() || "",
    longitude: sector?.longitude?.toString() || "",
    altitude: sector?.altitude?.toString() || "",
    cropTypeId: sector?.currentCropTypeId || "",
    cropStartDate: sector?.currentCropStartDate || "",
    cropPlantPopulation: sector?.currentCropPlantPopulation?.toString() || "",
    cropPlantingDate: sector?.currentCropPlantingDate || "",
  });
  const [controllerConfig, setControllerConfig] = useState<ControllerConfigValue>({
    controllerId: null,
    turnOnOutputIndex: null,
    turnOffOutputIndex: null,
    monitoringInputIndex: null,
    outputMode: "meta",
    inputMode: "digital",
  });

  const properties = useAtomValue(propertiesAtom);
  const deviceToLicMap = useAtomValue(deviceToLicMapAtom);
  const propertyDevices = useAtomValue(propertyDevicesAtom);
  const cropTypes = useAtomValue(cropTypesAtom);

  const isEditing = !!sector;

  // Get the LIC for the current project
  const projectLicId = useMemo(() => {
    const projectData = properties.flatMap((p) => p.projects).find((p) => p?.id === projectId);
    return projectData?.localized_irrigation_controller;
  }, [properties, projectId]);

  // Reset form data when sector prop changes or when creating new sector
  useEffect(() => {
    if (sector) {
      // Editing existing sector
      setFormData({
        name: sector.name || "",
        description: sector.description || "",
        area: sector.area?.toString() || "",
        power: sector.power?.toString() || "0",
        latitude: sector.latitude?.toString() || "",
        longitude: sector.longitude?.toString() || "",
        altitude: sector.altitude?.toString() || "",
        cropTypeId: sector.currentCropTypeId || "",
        cropStartDate: sector.currentCropStartDate || "",
        cropPlantPopulation: sector.currentCropPlantPopulation?.toString() || "",
        cropPlantingDate: sector.currentCropPlantingDate || "",
      });

      // Initialize controller config from currentVirtualDeviceConfig
      if (currentVirtualDeviceConfig) {
        setControllerConfig({
          controllerId: currentVirtualDeviceConfig.device.id,
          turnOnOutputIndex: currentVirtualDeviceConfig.turn_on_physical_output_index,
          turnOffOutputIndex: currentVirtualDeviceConfig.turn_off_physical_output_index,
          monitoringInputIndex: currentVirtualDeviceConfig.monitoring_physical_input_index,
          outputMode: currentVirtualDeviceConfig.mode,
          inputMode: currentVirtualDeviceConfig.monitoring_input_mode || "digital",
        });
      } else {
        // No current config - reset controller config
        setControllerConfig({
          controllerId: null,
          turnOnOutputIndex: null,
          turnOffOutputIndex: null,
          monitoringInputIndex: null,
          outputMode: "meta",
          inputMode: "digital",
        });
      }
    } else {
      // Creating new sector - reset form
      setFormData({
        name: "",
        description: "",
        area: "",
        power: hasAnalogPumpMode ? "50" : "0",
        latitude: "",
        longitude: "",
        altitude: "",
        cropTypeId: "",
        cropStartDate: "",
        cropPlantPopulation: "",
        cropPlantingDate: "",
      });

      // Reset controller config
      setControllerConfig({
        controllerId: null,
        turnOnOutputIndex: null,
        turnOffOutputIndex: null,
        monitoringInputIndex: null,
        outputMode: "meta",
        inputMode: "digital",
      });
    }
  }, [sector, currentVirtualDeviceConfig, hasAnalogPumpMode]);

  const handleInputChange = (field: string, value: string | number | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert("Por favor, preencha o nome do setor.");
      return;
    }

    // Validate controller configuration
    if (controllerConfig.controllerId && !controllerConfig.turnOnOutputIndex) {
      alert("Por favor, selecione a saída de ativação do controlador.");
      return;
    }

    // Validate crop population: required (total plant count) when a crop is selected
    if (formData.cropTypeId && formData.cropPlantPopulation.trim() === "") {
      alert("Por favor, informe a população total de plantas para a cultura selecionada.");
      return;
    }

    // Parse area string to number, default to 0 if empty or invalid
    const areaValue = formData.area.trim() !== "" ? parseFloat(formData.area) : 0;
    const finalAreaValue = Number.isNaN(areaValue) ? 0 : areaValue;

    // Parse power string to number, ensure it's within 0-100 range
    const powerValue = formData.power.trim() !== "" ? parseInt(formData.power, 10) : 0;
    const finalPowerValue = Math.max(0, Math.min(100, Number.isNaN(powerValue) ? 0 : powerValue));

    setSaving(true);
    try {
      const latitude = formData.latitude.trim() !== "" ? parseFloat(formData.latitude) : null;
      const longitude = formData.longitude.trim() !== "" ? parseFloat(formData.longitude) : null;
      const altValue = formData.altitude.trim() !== "" ? parseFloat(formData.altitude) : NaN;

      const coordinates =
        latitude !== null && longitude !== null && !isNaN(latitude) && !isNaN(longitude)
          ? { type: "Point" as const, coordinates: [longitude, latitude] as [number, number] }
          : null;
      const finalAltitude = !isNaN(altValue) ? altValue : null;

      const sectorData = {
        name: formData.name,
        description: formData.description,
        area: finalAreaValue,
        power: finalPowerValue,
        coordinates,
        altitude: finalAltitude,
        cropTypeId: formData.cropTypeId || null,
        cropStartDate: formData.cropStartDate || null,
        cropPlantPopulation:
          formData.cropPlantPopulation.trim() !== "" ? parseInt(formData.cropPlantPopulation, 10) : null,
        cropPlantingDate: formData.cropPlantingDate || null,
      };

      // Only pass controller config if a controller is selected
      const finalControllerConfig = controllerConfig.controllerId ? controllerConfig : null;

      onSave(sectorData, finalControllerConfig);
    } catch (error) {
      console.error("Error saving sector:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!sector || !onDelete) return;
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!sector || !onDelete) return;

    setDeleting(true);
    try {
      onDelete(sector.id);
    } catch (error) {
      console.error("Error deleting sector:", error);
    } finally {
      setDeleting(false);
    }
  };

  // Filter available valve controllers (only VC devices on the same mesh network as the project's LIC)
  // const availableValveControllers = useMemo(() => {
  //   // If there's no LIC for the project, no valve controller can be selected
  //   if (!projectLicId) return [];

  //   return devices.filter((device) => {
  //     // Only VC (valve controller) devices
  //     // Check if device.model is a string "VC" or an object with id "VC"
  //     const modelId = typeof device.model === "string" ? device.model : device.model?.id;
  //     if (modelId !== "VC") return false;

  //     // Mesh network check - device must be on the same mesh network as the project's LIC
  //     const deviceLicId = deviceToLicMap.get(device.id);
  //     return deviceLicId === projectLicId;
  //   });
  // }, [devices, projectLicId, deviceToLicMap]);

  // Get all mesh devices as available controllers for pumps
  const availableValveControllers = useMemo(() => {
    return propertyDevices.map((pd) => pd.device).filter((d) => isMeshDevice(d.model));
  }, [propertyDevices]);

  return (
    <>
      {/* Form Content */}
      <div className="space-y-6">
        {/* Sector Name */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Nome *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className="w-full px-4 py-4 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 min-h-12 bg-white"
            placeholder="Nome do setor"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">Descrição</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            className="w-full px-4 py-4 border border-neutral-200 rounded-xl text-base focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-150 resize-none bg-white"
            rows={3}
            placeholder="Descrição do setor"
          />
        </div>

        {/* Controller Configuration Panel */}
        <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Configuração do Controlador de Válvula</h3>
          {!projectLicId ? (
            <div className="p-4 border border-neutral-200 rounded-xl bg-neutral-50">
              <p className="text-sm text-neutral-500">
                Defina o Controlador de Irrigação Localizado (LIC) do projeto para configurar um controlador de válvula.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <ControllerConfigurationPanel
                equipmentType="valve"
                currentConfig={currentVirtualDeviceConfig}
                value={controllerConfig}
                onChange={setControllerConfig}
                availableControllers={availableValveControllers}
                disabled={!projectLicId}
                showTurnOffOutput={controllerConfig.outputMode === "pulse"}
                showMonitoringInput={true}
                showModeSelection={true}
              />

              {/* Power */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Potência (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.power}
                  onChange={(e) => handleInputChange("power", e.target.value)}
                  disabled={!hasAnalogPumpMode}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md outline-none ${
                    hasAnalogPumpMode
                      ? "focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                      : "bg-gray-100 text-gray-500 cursor-not-allowed"
                  }`}
                  placeholder="0"
                />
                {!hasAnalogPumpMode && (
                  <p className="text-xs text-neutral-500 mt-1">
                    A potência só pode ser ajustada quando a bomba de irrigação está configurada em modo analógico.
                  </p>
                )}
                {hasAnalogPumpMode && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Ajuste a potência da bomba (0-100%). Disponível apenas para bombas em modo analógico.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Características */}
        <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Características</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
              <input
                type="number"
                step="any"
                value={formData.latitude}
                onChange={(e) => handleInputChange("latitude", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                placeholder="-12.345678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
              <input
                type="number"
                step="any"
                value={formData.longitude}
                onChange={(e) => handleInputChange("longitude", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                placeholder="-45.678901"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Altitude (m)</label>
              <input
                type="number"
                step="any"
                value={formData.altitude}
                onChange={(e) => handleInputChange("altitude", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                placeholder="650"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Área (hectares)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.area}
                onChange={(e) => handleInputChange("area", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                placeholder="0.0"
              />
            </div>
          </div>
        </div>

        {/* Plantio */}
        <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Plantio</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Cultura</label>
              <select
                value={formData.cropTypeId}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    cropTypeId: value,
                    cropStartDate: value ? prev.cropStartDate || new Date().toISOString().split("T")[0] : "",
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
              >
                <option value="">Selecione a cultura</option>
                {cropTypes.map((ct) => (
                  <option key={ct.id} value={ct.id}>
                    {ct.name}
                  </option>
                ))}
              </select>
            </div>
            {formData.cropTypeId && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data de Plantio</label>
                  <input
                    type="date"
                    value={formData.cropPlantingDate}
                    onChange={(e) => handleInputChange("cropPlantingDate", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">População (total de plantas)</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.cropPlantPopulation}
                    onChange={(e) => handleInputChange("cropPlantPopulation", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none bg-white"
                    placeholder=""
                  />
                  <p className="text-xs text-gray-500 mt-1">Número absoluto de plantas do setor (não por hectare).</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Device Activation Panel - only show if sector has a configured controller */}
      {sector?.id && controllerConfig.controllerId && controllerConfig.turnOnOutputIndex != null && (
        <div className="mt-6">
          <DeviceActivationPanel
            elementType="valve"
            elementId={sector.id}
            elementControllerId={controllerConfig.controllerId}
            turnOnDurationMinutes={1}
            title="Testar Acionamento da Válvula"
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="pt-6 border-t border-neutral-100">
        <div className="flex gap-4">
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={deleting}
            variant="primary"
            size="md"
            className="flex-1 rounded-xl"
          >
            Salvar
          </Button>

          {isEditing && onDelete && (
            <Button
              onClick={handleDelete}
              loading={deleting}
              disabled={saving}
              variant="destructive"
              size="md"
              className="flex-1 rounded-xl"
            >
              Excluir
            </Button>
          )}

          <Button
            onClick={onCancel}
            disabled={saving || deleting}
            variant="secondary"
            size="md"
            className="flex-1 rounded-xl"
          >
            Cancelar
          </Button>
        </div>
      </div>
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Excluir Setor"
        message={`Tem certeza que deseja excluir o setor "${sector?.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="danger"
      />
    </>
  );
}

export default SectorDetailPanel;
